import { selectFeatureFlags } from 'core/userContext';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { CHECKBOX_STATUS } from 'ui/checkbox/checkboxStatus';
import { CheckboxGridCell } from 'ui/indexPages/tables/cells';
import { GridRow } from 'ui/indexPages/tables/grid';
import { Link } from 'ui/links';
import { StatusHint } from 'ui/statusHint';
import { body2, spacing } from 'ui/theme';
import { RightTooltip } from 'ui/tooltip';
import { DATE_FORMAT_FULL_WITH_HOURS_MINS_SECONDS, displayDate, recencyInDays } from 'utils/date';
import { CUSTOM_EVENTS_GRIDS } from '../constants';
import { useCustomEventsQueryContext } from '../contexts';
import { selectIsErrorDeletionPolicies, selectIsLoadingDeletionPolicies } from '../selectors';
import { INGESTION_POLICIES, MergedCustomEvent } from '../types';
import { formatLastSeen, formatReferences, formatRetentionPolicy } from '../utils';
import { CustomEventIndexOverflowMenu } from './CustomEventIndexOverflowMenu';
import { CustomEventsLoadableTableCell as LoadableCell } from './CustomEventsLoadableTableCell';

const Cell = styled.div`
  align-items: center;
  display: flex;
  ${body2}
`;

const NameCell = styled.div`
  align-items: center;
  display: flex;
  word-break: break-all;
`;

const RecencyHint = styled(StatusHint)`
  margin-right: ${spacing.half};
`;

const TooltipTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

type Props = {
  customEvent: MergedCustomEvent;
  href?: string;
  ingestionPolicyFilter: INGESTION_POLICIES;
  selected: (string | number)[];
  toggleSelection: (id: string) => void;
};

export const CustomEventsTableRow = ({
  customEvent,
  href,
  ingestionPolicyFilter,
  selected,
  toggleSelection,
}: Props) => {
  const { isErrorCustomEvents, isLoadingCustomEvents } = useCustomEventsQueryContext();
  const isLoadingDeletionPolicies = useSelector(selectIsLoadingDeletionPolicies);
  const isErrorDeletionPolicies = useSelector(selectIsErrorDeletionPolicies);
  const { CustomEventsPageModernized, SchemaManagementUI } = useSelector(selectFeatureFlags);

  const {
    eventName,
    docCount,
    lastSeen,
    references,
    listReferences,
    workflowReferences,
    workflowTriggers,
    retentionPolicy,
  } = customEvent;

  const isOverflowMenuOptionsEnabled = !!CustomEventsPageModernized || !!SchemaManagementUI;
  const referencesText = formatReferences(references, 'reference');
  const lastSeenText = formatLastSeen(lastSeen);
  const lastSeenTimestamp = displayDate(lastSeen, DATE_FORMAT_FULL_WITH_HOURS_MINS_SECONDS);
  const retentionPolicyText = formatRetentionPolicy(retentionPolicy);
  const checkboxStatus = selected.includes(eventName)
    ? CHECKBOX_STATUS.CHECKED
    : CHECKBOX_STATUS.UNCHECKED;

  return (
    <GridRow
      data-test="custom-event-row"
      gridLayout={CUSTOM_EVENTS_GRIDS}
      key={customEvent.eventName}
    >
      <CheckboxGridCell
        handleClick={() => toggleSelection(eventName)}
        id={eventName}
        status={checkboxStatus}
      />
      <NameCell>
        {href ? (
          <Link inTable useReactRouter href={href}>
            {eventName}
          </Link>
        ) : (
          <span>{eventName}</span>
        )}
      </NameCell>
      <LoadableCell isError={isErrorCustomEvents} isLoading={isLoadingCustomEvents}>
        <RightTooltip tooltipContent={lastSeenTimestamp} isUnderlined isEnabled={!!lastSeen}>
          <RecencyHint active={recencyInDays(lastSeen) < 1}></RecencyHint>
          <span>{lastSeenText}</span>
        </RightTooltip>
      </LoadableCell>
      <LoadableCell isError={isErrorCustomEvents} isLoading={isLoadingCustomEvents}>
        <span>{docCount.toLocaleString()}</span>
      </LoadableCell>
      <LoadableCell isError={isErrorCustomEvents} isLoading={isLoadingCustomEvents}>
        <RightTooltip
          isEnabled={!!references}
          isUnderlined
          tooltipContent={
            <TooltipTextContainer>
              <div>{formatReferences(workflowReferences, 'journey reference')}</div>
              {workflowTriggers.length > 0 && (
                <div>({formatReferences(workflowTriggers.length, 'journey')} triggered)</div>
              )}
              <div>{formatReferences(listReferences, 'list reference')}</div>
            </TooltipTextContainer>
          }
        >
          <span>{referencesText}</span>
        </RightTooltip>
      </LoadableCell>
      <LoadableCell isError={isErrorDeletionPolicies} isLoading={isLoadingDeletionPolicies}>
        <span>{retentionPolicyText}</span>
      </LoadableCell>
      <Cell>
        <CustomEventIndexOverflowMenu
          eventName={eventName}
          hasReferences={Boolean(references)}
          ingestionPolicyFilter={ingestionPolicyFilter}
          isEnabled={isOverflowMenuOptionsEnabled}
        />
      </Cell>
    </GridRow>
  );
};
